import { Injectable } from '@angular/core';
import { produce } from 'immer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, take } from 'rxjs/operators';
import {
  DataPoint,
  FormatComboTierSpendLevelOptions,
  Formatter,
  formatComboTierSpendLevel,
  formatPlannedSpendLevel,
  PlannedSpendLevel,
  SpendLevel,
  SpendLevelTypesMap,
  TierFieldNames,
  normalizeData
} from '@aptitude/shared-vanilla';

import { Features } from '../../types/types';
import { FeatureListService } from '../../services/feature-list.service';
import { SpendWithMarketshare } from './extras/spend-with-market-share.interface';
import {
  PlanImpact,
  PlannedSpend,
} from '../../api/types/rfp/plan-impact.interface';
import { Type } from '../../api/graphql/purchaseplan/purchaseplan';
import { SelectedTier } from '@aptitude/shared';
@Injectable({
  providedIn: 'root',
})
export class TargetAndSpendService {
  featuresList?: Features;

  constructor(featureListService: FeatureListService) {
    featureListService?.aptitudeFeatureList$
      ?.pipe(
        take(1),
        map((featuresList: Features) => (this.featuresList = featuresList)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  getTargetOverview(
    selectedTier: SelectedTier,
    spendWithMarketshare: SpendWithMarketshare,
    fieldNames: string[],
  ): Array<DataPoint> {
    return fieldNames.map((fieldName: string) => {
      switch (fieldName) {
        case 'dollarMarketShare':
          return {
            value: spendWithMarketshare.spendMarketShare,
            target: selectedTier.dollarMarketShare,
          };
        case 'unitMarketShare':
          return {
            value: spendWithMarketshare.unitMarketShare,
            target: selectedTier.unitMarketShare,
          };
        case 'absoluteDollars':
          return {
            value: spendWithMarketshare.spendVolume,
            target: selectedTier.absoluteDollars,
          };
        case 'absoluteUnits':
          return {
            value: spendWithMarketshare.unitVolume,
            target: selectedTier.absoluteUnits,
          };
        default:
          return {
            value: 0,
            target: 0,
          };
      }
    });
  }

  getTargetValue(
    selectedTier: SelectedTier,
    plannedSpend: PlannedSpend,
    tierName = '',
  ) {
    let clonedSelectedTier: SpendLevel = { ...(selectedTier as SpendLevel) };
    const targetTier = {
      dollarMarketShare: 0,
      unitMarketShare: 0,
      absoluteDollars: 0,
      absoluteUnits: 0,
    };

    clonedSelectedTier?.spendLevelType?.fieldNames.forEach(
      (fieldName: string) => {
        switch (fieldName) {
          case 'dollarMarketShare':
            clonedSelectedTier = {
              ...clonedSelectedTier,
              dollarMarketShare: plannedSpend.spendMarketShare,
              target: {
                ...targetTier,
                dollarMarketShare: selectedTier.dollarMarketShare ?? 0,
              },
            };
            break;
          case 'unitMarketShare':
            clonedSelectedTier = {
              ...clonedSelectedTier,
              unitMarketShare: plannedSpend.unitMarketShare,
              target: {
                ...targetTier,
                unitMarketShare: selectedTier.unitMarketShare ?? 0,
              },
            };
            break;
          case 'absoluteDollars':
            clonedSelectedTier = {
              ...clonedSelectedTier,
              absoluteDollars: plannedSpend.spendVolume,
              target: {
                ...targetTier,
                absoluteDollars: selectedTier.absoluteDollars ?? 0,
              },
            };
            break;
          case 'absoluteUnits':
            clonedSelectedTier = {
              ...clonedSelectedTier,
              absoluteUnits: plannedSpend.unitVolume,
              target: {
                ...targetTier,
                absoluteUnits: selectedTier.absoluteUnits ?? 0,
              },
            };
            break;
        }
      },
    );

    return this.formatComboTierSpendLevel(clonedSelectedTier, {
      truncateValues: true,
      appendTierName: true,
      useZeroValueLabel: false,
      tierName,
      isSpendValue: true,
    });
  }

  getTargetFull(selectedTier: SelectedTier) {
    return this.formatComboTierSpendLevel(selectedTier as SpendLevel, {
      sizeOfSpendTypeLabel: 'SHORT_DISPLAY_NAME',
      truncateValues: true,
      appendTierName: true,
      useZeroValueLabel: false,
      tierName: '',
    });
  }

  getTargetColumnName(spendLevelType: Type, position: number) {
    const fieldName: TierFieldNames = spendLevelType?.fieldNames?.[
      position
    ] as TierFieldNames;
    return SpendLevelTypesMap[fieldName]?.['SHORT_DISPLAY_NAME'];
  }

  getTargetStatusText(
    selectedTier: SelectedTier,
    plannedSpend: PlannedSpend,
  ): string {
    const tragetStatusTexts = ['Under Target', 'Near Target', 'On Target'];
    const targetStatusNumbers = [0.9, 0.97, 1];
    const data = normalizeData(this.getTargetOverview(
      selectedTier,
      plannedSpend as SpendWithMarketshare,
      selectedTier?.spendLevelType?.fieldNames || [],
    ));

    const indexOperatorValue =
      selectedTier?.spendLevelType?.operator?.toLowerCase() === 'or'
        ? Math.max(...data.map((item) => item.value)) ?? 0
        : Math.min(...data.map((item) => item.value)) ?? 0;

    let indexRangeText = 0;
    indexRangeText = targetStatusNumbers.findIndex((value, index, array) => {
      return indexOperatorValue >= value && indexOperatorValue < array[index + 1];
    }) + 1;

    return tragetStatusTexts[indexRangeText];
  }

  normalizeData(data: Array<DataPoint>) {
    return normalizeData(data)
  }

  getFormattedSpendLevelType(
    SpendLevelValue: number,
    fieldName: string,
    attribute = 'value',
  ) {
    return this.formatPlannedSpendLevel(
      SpendLevelValue,
      fieldName,
      false,
      Formatter.currency,
      Formatter.percent,
      (value: number | null) =>
        new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
          value as number,
        ),
      attribute === 'value',
      '',
    );
  }

  formatSpendLevelTextActualValue(
    selectedTier: SelectedTier,
    plannedSpend: PlannedSpend,
  ) {
    return this.getTargetValue(selectedTier, plannedSpend, '');
  }

  /**
   * This function is a wrapper of formatComboTierSpendLevel in  format-combo-tier-spend-level.ts
   * It is a work arround until we migrate angularJS, as we share this function
   */
  formatComboTierSpendLevel(
    spendLevel: SpendLevel = { type: { fieldNames: [] as TierFieldNames[] } },
    formattingOptions: FormatComboTierSpendLevelOptions = {
      truncateValues: false,
      useZeroValueLabel: false,
    },
  ) {
    return formatComboTierSpendLevel(
      spendLevel,
      formattingOptions,
      Formatter.currency,
      Formatter.percent,
      (value: number | null) =>
        new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
          value as number,
        ),
    );
  }

  /**
   * This function is a wrapper of formatPlannedSpendLevel in format-planned-spend-level.ts
   * It is a work arround until we migrate angularJS, as we share this function
   */
  formatPlannedSpendLevel(
    value: number | null,
    type: string,
    truncateValue = false,
    formatMoney: (arg1: number | null) => string,
    formatPercent: (arg1: number | null) => string,
    formatNumberInner: (arg1: number | null) => string,
    isSpendValue = false,
    operator = '',
  ): string {
    return formatPlannedSpendLevel(
      value,
      type,
      truncateValue,
      formatMoney,
      formatPercent,
      formatNumberInner,
      isSpendValue,
      operator,
      true,
    );
  }

  getTargetOverviewSeller(planImpact?: PlanImpact) {
    // TODO
    // Generate new data points and save the structure off (just so we don't end up with )
    const data = planImpact?.selectedTier?.spendLevelType.fieldNames.map(
      (fieldName: string) => {
        switch (fieldName) {
          case 'dollarMarketShare':
            return {
              value: planImpact.plannedSpendLevel.spendMarketShare,
              target: planImpact.selectedTier?.dollarMarketShare,
            };
          case 'unitMarketShare':
            return {
              value: planImpact.plannedSpendLevel.unitMarketShare,
              target: planImpact.selectedTier?.unitMarketShare,
            };
          case 'absoluteDollars':
            return {
              value: planImpact.plannedSpendLevel.spendVolume,
              target: planImpact.selectedTier?.absoluteDollars,
            };
          case 'absoluteUnits':
            return {
              value: planImpact.plannedSpendLevel.unitVolume,
              target: planImpact.selectedTier?.absoluteUnits,
            };
          default:
            return {};
        }
      },
    ) as Array<DataPoint> | undefined;

    return data;
  }

  getTargetFullSeller(planImpact: PlanImpact) {
    const value = this.formatComboTierSpendLevel(
      planImpact.selectedTier as SpendLevel,
      {
        sizeOfSpendTypeLabel: 'SHORT_DISPLAY_NAME',
        truncateValues: true,
        appendTierName: true,
        useZeroValueLabel: false,
        tierName: '',
      },
    );

    return value;
  }

  getTargetValueSeller(planImpact: PlanImpact) {
    // Create something that looks like a spend level that we could turn around and feed to
    // the same formatComboTierSpendLevelFilter we use to translate the tier to a human
    // readable form.
    const fakeTier: SpendLevel = produce(
      planImpact?.selectedTier,
      (draft: SpendLevel) => {
        draft?.spendLevelType?.fieldNames.map((fieldName) => {
          // Based on the field name, go find the current value from our current counts and totals
          // and our target value from the spendLevelType.
          //
          // There's no need to pay any attention to the operator because that doesn't really show up
          // in the graph.
          switch (fieldName) {
            case 'dollarMarketShare':
              draft.dollarMarketShare =
                planImpact.plannedSpendLevel.spendMarketShare;
              break;
            case 'unitMarketShare':
              draft.unitMarketShare =
                planImpact.plannedSpendLevel.unitMarketShare;
              break;
            case 'absoluteDollars':
              draft.absoluteDollars = planImpact.plannedSpendLevel.spendVolume;
              break;
            case 'absoluteUnits':
              draft.absoluteUnits = planImpact.plannedSpendLevel.unitVolume;
              break;
          }
        });
      },
    ) as SpendLevel;

    const value = this.formatComboTierSpendLevel(fakeTier, {
      truncateValues: true,
      appendTierName: true,
      useZeroValueLabel: false,
      tierName: '',
    });

    return value;
  }

  spendWithMarketShareConverter(
    spendValues: PlannedSpendLevel,
  ): SpendWithMarketshare {
    return {
      spendMarketShare: spendValues.dollarMarketShare,
      unitMarketShare: spendValues.unitMarketShare,
      spendVolume: spendValues.absoluteDollars,
      unitVolume: spendValues.absoluteUnits,
    };
  }
}
